'use client';

import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import {
  type ComponentPropsWithRef,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import s from './Checkbox.module.css';

const checkboxVariants = ['default', 'error'] as const;
type CheckboxVariants = (typeof checkboxVariants)[number];

export interface CheckboxProps extends ComponentPropsWithRef<'input'> {
  ariaDisabled?: boolean;
  className?: string;
  disabled?: boolean;
  id: string;
  isIndeterminate?: boolean;
  name: string;
  value?: string;
  variant?: CheckboxVariants;
}

const checkboxStyleVariants = cva(s.checkbox, {
  variants: {
    isIndeterminate: {
      true: s.isIndeterminate,
    },
    variant: {
      default: '',
      error: s.errorVariant,
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export const Checkbox = (props: CheckboxProps) => {
  const {
    ariaDisabled,
    className,
    disabled = false,
    isIndeterminate = false,
    ref: externalRef,
    variant = 'default',
    ...attrs
  } = props;

  const internalRef = useRef<HTMLInputElement>(null);

  const mergeRefs = useCallback(
    (element: HTMLInputElement | null) => {
      // Update internal ref
      internalRef.current = element;

      // Update external ref if provided
      if (externalRef) {
        if (typeof externalRef === 'function') {
          externalRef(element);
        } else if (externalRef && typeof externalRef === 'object') {
          externalRef.current = element;
        }
      }
    },
    [externalRef],
  );

  useEffect(() => {
    if (internalRef.current) {
      internalRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const appearsDisabled = ariaDisabled || disabled || undefined;

  return (
    <input
      {...attrs}
      ref={mergeRefs}
      aria-disabled={appearsDisabled}
      className={clsx(
        checkboxStyleVariants({
          isIndeterminate,
          variant,
          className,
        }),
      )}
      disabled={disabled}
      type="checkbox"
    />
  );
};
